import React from 'react';
import { Box, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartDataPoint } from '../../types/admin';

interface UsageChartProps {
  data: ChartDataPoint[];
  title: string;
  height?: number;
  color?: string;
}

// TODO: Replace with actual chart library (recharts) once installed
// npm install recharts
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export const UsageChart: React.FC<UsageChartProps> = ({
  data,
  title,
  height = 300,
  color = '#2E5BBA'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // データを Recharts 用に変換
  const chartData = data.map(point => ({
    date: new Date(point.date).toLocaleDateString('ja-JP', { 
      month: isMobile ? 'numeric' : 'short', 
      day: 'numeric' 
    }),
    value: point.value,
    fullDate: point.date,
    label: point.label
  }));

  const maxValue = Math.max(...data.map(d => d.value), 1);
  const minValue = Math.min(...data.map(d => d.value), 0);

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, height: height + (isMobile ? 60 : 80) }}>
      <Typography 
        variant="h6" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          fontWeight: 600,
        }}
      >
        {title}
      </Typography>
      
      <Box sx={{ mt: 1 }}>
        <Typography 
          variant="caption" 
          color="textSecondary"
          sx={{ 
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            display: { xs: 'none', sm: 'block' }
          }}
        >
          データポイント: {data.length}件 | 最大値: {maxValue} | 最小値: {minValue}
        </Typography>
      </Box>

      <Box sx={{ width: '100%', height: height, mt: { xs: 1, sm: 2 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ 
              top: 20, 
              right: isMobile ? 15 : 30, 
              left: isMobile ? 15 : 20, 
              bottom: isMobile ? 15 : 20 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="date" 
              fontSize={isMobile ? 10 : 12}
              tick={{ fill: '#666' }}
              interval="preserveStartEnd"
            />
            <YAxis 
              fontSize={isMobile ? 10 : 12}
              tick={{ fill: '#666' }}
              domain={['dataMin - 1', 'dataMax + 1']}
              width={isMobile ? 40 : 50}
            />
            <Tooltip 
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #ddd',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: isMobile ? '12px' : '14px'
              }}
              formatter={(value: any, name: any) => [
                `${value}件`,
                'チャット数'
              ]}
              labelFormatter={(label) => `日付: ${label}`}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke={color} 
              strokeWidth={isMobile ? 2 : 3}
              dot={{ 
                fill: color, 
                strokeWidth: 2, 
                r: isMobile ? 3 : 4 
              }}
              activeDot={{ 
                r: isMobile ? 5 : 6, 
                stroke: color, 
                strokeWidth: 2,
                fill: '#fff'
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};